import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { apiExportData, apiDeleteHistory, apiDeleteAccount, apiBillingPortal, apiMyExpertRequests, apiBuyCredits, apiCheckout, fetchHistory } from "../lib/api";
import { Download, Trash2, ShieldOff, Loader2, ShieldCheck, Info, Crown, ArrowUpRight, UserCheck, Settings, History as HistoryIcon, Check, Search } from "lucide-react";
import { statusFor } from "../lib/status";
import { FamilyManager } from "../components/FamilyManager";

const CREDIT_PACKS = [
  { pack: "1", credits: 1, price: "€2,99" },
  { pack: "3", credits: 3, price: "€7,99" },
  { pack: "5", credits: 5, price: "€11,99" },
  { pack: "10", credits: 10, price: "€15", best: true },
];

export default function Account() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [info, setInfo] = useState("");
  const [expert, setExpert] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => { if (user === null) navigate("/login"); }, [user, navigate]);
  useEffect(() => {
    if (!user) return;
    apiMyExpertRequests().then(setExpert).catch(() => setExpert({ items: [], credits: 0 }));
    fetchHistory().then((d) => setHistory(d.items || [])).catch(() => setHistory([]));
  }, [user]);
  if (!user) return null;

  const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("nl-BE", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  const buyCredits = async (pack) => {
    setBusy(`credits-${pack}`);
    try {
      const { checkout_url } = await apiBuyCredits(pack, window.location.origin);
      window.location.href = checkout_url;
    } catch (_) { setBusy(""); }
  };

  const exportData = async () => {
    setBusy("export"); setInfo("");
    try {
      const data = await apiExportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `trustwall-data-${Date.now()}.json`; a.click();
      URL.revokeObjectURL(url);
      setInfo(t("account.done"));
    } catch (_) { /* ignore */ } finally { setBusy(""); }
  };

  const delHistory = async () => {
    if (!window.confirm(t("account.confirmDelHistory"))) return;
    setBusy("history"); setInfo("");
    try { await apiDeleteHistory(); setInfo(t("account.done")); } catch (_) { /* ignore */ } finally { setBusy(""); }
  };

  const delAccount = async () => {
    if (!window.confirm(t("account.confirmDelAccount"))) return;
    setBusy("account");
    try { await apiDeleteAccount(); logout(); navigate("/"); } catch (_) { setBusy(""); }
  };

  const manageSub = async () => {
    setBusy("portal");
    try {
      const { portal_url } = await apiBillingPortal(window.location.origin);
      window.location.href = portal_url;
    } catch (_) { setBusy(""); }
  };

  const upgradeSub = async () => {
    setBusy("upgrade");
    try {
      const { checkout_url } = await apiCheckout(window.location.origin);
      window.location.href = checkout_url;
    } catch (_) { setBusy(""); }
  };

  const isPro = user.plan === "pro";
  const isFamily = user.plan === "family";
  const isOwner = isFamily && user.family_role === "owner";
  const hasOwnSub = isPro || isOwner;
  const isPaid = isPro || isFamily;
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("nl-BE", { dateStyle: "medium" }); } catch { return iso; } };
  const card = "rounded-2xl border border-slate-200 bg-white p-6";

  return (
    <div className="tw-grid-bg min-h-screen px-4 pt-28 pb-16 sm:px-6 lg:px-8" data-testid="account-page">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#0D9488]">
            <ShieldCheck className="h-6 w-6 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="account-title">{t("account.dataTitle")}</h1>
            <p className="text-sm text-slate-500">{t("account.dataSubtitle")}</p>
          </div>
        </div>

        <div className="mt-6 flex flex-col items-start justify-between gap-4 rounded-2xl border border-blue-200 bg-blue-50 p-6 sm:flex-row sm:items-center" data-testid="account-check-cta">
          <div>
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-blue-700" />
              <h2 className="font-semibold text-slate-900">{t("account.hubCheckTitle")}</h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">{t("account.hubCheckDesc")}</p>
          </div>
          <button data-testid="account-check-btn" onClick={() => navigate("/check")}
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#1E3A8A] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8]">
            <ShieldCheck className="h-4 w-4" /> {t("account.hubCheckBtn")}
          </button>
        </div>

        <div className={`mt-4 ${isPaid ? "rounded-2xl border border-blue-300 bg-blue-50 p-6" : card}`} data-testid="account-subscription">
          <div className="mb-3 flex items-center gap-2">
            <Settings className="h-4 w-4 text-blue-700" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t("account.subManageTitle")}</h2>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                {isPaid && <Crown className="h-5 w-5 text-blue-700" />}
                <h3 className="font-semibold text-slate-900">{t("account.planLabel")}: {isFamily ? t("account.planFamily") : isPro ? t("account.planPro") : t("account.planFree")}{isFamily && user.family_role === "member" ? ` (${t("account.family.memberBadge")})` : ""}</h3>
              </div>
              {hasOwnSub && user.current_period_end && (
                <p className="mt-1 text-sm text-slate-500">
                  {user.cancel_at_period_end ? t("account.proCancels") : t("account.proUntil")}: {fmtDate(user.current_period_end)}
                </p>
              )}
            </div>
            {hasOwnSub ? (
              <button data-testid="account-manage-sub" onClick={manageSub} disabled={busy === "portal"}
                className="inline-flex shrink-0 items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{t("account.manageSub")}
              </button>
            ) : isFamily ? null : (
              <button data-testid="account-upgrade" onClick={upgradeSub} disabled={busy === "upgrade"}
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#1E3A8A] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-60">
                {busy === "upgrade" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{t("account.upgradeCta")} <ArrowUpRight className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="account-sub-hint">{hasOwnSub ? t("account.manageHint") : isFamily ? t("account.family.memberSubHint") : t("account.upgradeHint")}</p>
          {!isPaid && (
            <button data-testid="account-view-plans" onClick={() => navigate("/pricing")}
              className="mt-2 text-xs font-medium text-blue-700 hover:text-blue-700">{t("account.viewPricing")}</button>
          )}

          {hasOwnSub && user.cancel_at_period_end && user.current_period_end && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="account-downgrade-banner">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t("account.proCancelLead")} <strong>{fmtDate(user.current_period_end)}</strong>. {t("account.proCancelTail")}</span>
            </div>
          )}

          {/* Free vs Pro comparison (hidden for Family) */}
          {!isFamily && (
          <div className="mt-5 grid grid-cols-2 gap-3" data-testid="account-plan-compare">
            <div className={`rounded-xl border p-4 ${!isPro ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-slate-50"}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-900">{t("account.planFree")}</span>
                {!isPro && <span className="rounded-full bg-[#1E3A8A] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">{t("account.cmpCurrent")}</span>}
              </div>
              <ul className="mt-3 space-y-1.5">
                {t("account.cmpFree").map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />{f}</li>
                ))}
              </ul>
            </div>
            <div className={`rounded-xl border p-4 ${isPro ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-slate-50"}`}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm font-bold text-slate-900"><Crown className="h-4 w-4 text-blue-700" />{t("account.planPro")}</span>
                {isPro && <span className="rounded-full bg-[#1E3A8A] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">{t("account.cmpCurrent")}</span>}
              </div>
              <ul className="mt-3 space-y-1.5">
                {t("account.cmpPro").map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-700" />{f}</li>
                ))}
              </ul>
            </div>
          </div>
          )}
        </div>

        {isFamily && <FamilyManager t={t} />}

        <div className="mt-4 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
          <span>{t("account.retentionNote")}</span>
        </div>

        {info && <p data-testid="account-info" className="mt-4 rounded-lg bg-emerald-50 px-4 py-2 text-sm text-emerald-700">{info}</p>}

        <div className="mt-6 space-y-4">
          <div className={card} data-testid="account-expert">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-blue-700" />
              <h2 className="font-semibold text-slate-900">{t("account.expertTitle")}</h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">{t("account.expertDesc")}</p>
            <div>
              <button data-testid="account-start-expert" onClick={() => navigate("/check?expert=1")}
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8]">
                <UserCheck className="h-4 w-4" /> {t("account.startExpert")}
              </button>
            </div>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm text-blue-800" data-testid="account-credits">
              {t("expert.creditsLeft")}: <span className="font-bold">{expert ? expert.credits : (user.expert_credits || 0)}</span>
            </div>
            {user.expert_included_total > 0 && (
              <p className="mt-2 text-sm text-slate-500" data-testid="account-included-credits">
                {t("account.includedCredits").replace("{remaining}", user.expert_included_remaining ?? 0).replace("{total}", user.expert_included_total)}
                {user.current_period_end && ` · ${t("account.includedReset")} ${fmtDate(user.current_period_end)}`}
              </p>
            )}
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {CREDIT_PACKS.map((p) => (
                <button key={p.pack} data-testid={`buy-credits-${p.pack}`} onClick={() => buyCredits(p.pack)} disabled={busy === `credits-${p.pack}`}
                  className={`relative flex flex-col items-center rounded-xl border px-3 py-3 text-center transition-colors disabled:opacity-60 ${p.best ? "border-sky-500/50 bg-blue-50 hover:bg-blue-100" : "border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50"}`}>
                  {p.best && <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#1E3A8A] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">{t("account.bestDeal")}</span>}
                  <span className="text-lg font-bold text-slate-900">{p.credits} {p.credits === 1 ? t("account.creditSingular") : t("account.creditPlural")}</span>
                  <span className="mt-0.5 text-sm text-blue-700">{p.price}</span>
                </button>
              ))}
            </div>
            {expert && expert.items && expert.items.length > 0 && (
              <div className="mt-5 space-y-3" data-testid="account-expert-list">
                <h3 className="text-sm font-semibold text-slate-600">{t("account.expertMyReviews")}</h3>
                {expert.items.map((r) => (
                  <div key={r.request_id} className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`expert-review-${r.request_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-sm text-slate-600">{r.masked_preview || t(`check.tabs.${r.input_type}`)}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.status === "reviewed" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-800"}`}>
                        {t(`expert.status.${r.status}`)}
                      </span>
                    </div>
                    {r.status === "reviewed" && (
                      <div className="mt-3 rounded-lg bg-slate-100 p-3">
                        <p className="text-sm font-bold text-slate-900">{t("expert.verdict")}: {r.expert_risk_level ? t(`common.${r.expert_risk_level.toLowerCase()}`) : "—"}</p>
                        {r.expert_summary && <p className="mt-1 text-sm text-slate-600">{r.expert_summary}</p>}
                        {r.expert_advice && <p className="mt-1 text-sm text-slate-500"><span className="font-medium text-slate-600">{t("expert.adviceLabel")}: </span>{r.expert_advice}</p>}
                      </div>
                    )}
                    {r.status === "in_review" && <p className="mt-2 text-xs text-slate-500">{t("expert.sla")}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={card} data-testid="account-history">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <HistoryIcon className="h-5 w-5 text-blue-700" />
                <h2 className="font-semibold text-slate-900">{t("account.recentTitle")}</h2>
              </div>
              <Link to="/history" data-testid="account-view-history" className="shrink-0 text-xs font-medium text-blue-700 hover:text-blue-700">{t("account.viewAllHistory")}</Link>
            </div>
            {history === null ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-blue-700" /></div>
            ) : history.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">{t("account.recentEmpty")}</p>
            ) : (
              <div className="mt-3 space-y-2">
                {history.slice(0, 5).map((it, i) => {
                  const s = statusFor(it.risk_level || "LIMITED");
                  const Icon = s.icon;
                  return (
                    <button key={it.analysis_id} data-testid={`account-history-item-${i}`} onClick={() => navigate("/history")}
                      className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-left transition-colors hover:bg-slate-100">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${s.bg} ring-1 ${s.ring}`}>
                        <Icon className={`h-4 w-4 ${s.text}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${s.text}`}>{it.risk_level ? t(`common.${it.risk_level.toLowerCase()}`) : "—"}</span>
                          <span className="text-xs text-slate-600">· {fmtDateTime(it.created_at)}</span>
                        </div>
                        <p className="truncate text-xs text-slate-500">{it.preview || t(`check.tabs.${it.input_type}`)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className={card} data-testid="account-export">
            <h2 className="font-semibold text-slate-900">{t("account.exportTitle")}</h2>
            <p className="mt-1 text-sm text-slate-500">{t("account.exportDesc")}</p>
            <button data-testid="account-export-btn" onClick={exportData} disabled={busy === "export"}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-60">
              {busy === "export" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {t("account.exportBtn")}
            </button>
          </div>

          <div className={card} data-testid="account-del-history">
            <h2 className="font-semibold text-slate-900">{t("account.delHistoryTitle")}</h2>
            <p className="mt-1 text-sm text-slate-500">{t("account.delHistoryDesc")}</p>
            <button data-testid="account-del-history-btn" onClick={delHistory} disabled={busy === "history"}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
              {busy === "history" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {t("account.delHistoryBtn")}
            </button>
          </div>

          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.06] p-6" data-testid="account-del-account">
            <h2 className="font-semibold text-rose-300">{t("account.delAccountTitle")}</h2>
            <p className="mt-1 text-sm text-slate-500">{t("account.delAccountDesc")}</p>
            <button data-testid="account-del-account-btn" onClick={delAccount} disabled={busy === "account"}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-slate-900 hover:bg-rose-500 disabled:opacity-60">
              {busy === "account" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />} {t("account.delAccountBtn")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
